import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchAllItems } from '../services/api';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ZapIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const FlaskIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const DatabaseIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s8-1.79 8-4" />
  </svg>
);

const FlowIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const Search = ({ user }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      setRecentSearches(JSON.parse(stored));
    }
  }, []);

  // Auto-focus the search input
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.trim().length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const searchResults = await searchAllItems(user.id, searchQuery); 
        setResults(searchResults);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [user?.id]
  );

  // Handle search query changes
  useEffect(() => {
    if (query.trim()) {
      setShowSuggestions(true);
      debouncedSearch(query);
    } else {
      setResults([]);
      setLoading(false);
      setSelectedIndex(-1);
    }
  }, [query, debouncedSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle result click
  const handleResultClick = (result) => {
    // Save to recent searches
    const newRecentSearches = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(newRecentSearches);
    localStorage.setItem('recentSearches', JSON.stringify(newRecentSearches));

    // Navigate to the result
    navigate(result.path);
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm) => {
    setQuery(searchTerm);
    setShowSuggestions(true);
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // Get icon for result type
  const getResultIcon = (type) => {
    switch (type) {
      case 'project':
        return <FolderIcon />;
      case 'prompt':
        return <ZapIcon />;
      case 'experiment':
        return <FlaskIcon />;
      case 'testSet':
        return <DatabaseIcon />;
      case 'flow':
        return <FlowIcon />;
      default:
        return <FolderIcon />;
    }
  };

  // Get type label for result
  const getTypeLabel = (type) => {
    switch (type) {
      case 'project':
        return 'Project';
      case 'prompt':
        return 'Prompt';
      case 'experiment':
        return 'Experiment';
      case 'testSet':
        return 'Test Set';
      case 'flow':
        return 'Flow';
      default:
        return 'Item';
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-celadon-50/30">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {/* Back Button */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 shadow-soft hover:shadow-medium group"
            >
              <ArrowLeftIcon className="mr-2 group-hover:-translate-x-0.5 transition-transform duration-200" />
              Back to Dashboard
            </button>
          </div>

         <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Search</h1>
            <p className="text-xl text-gray-600">Find your projects, prompts, experiments, test sets, and flows</p>
          </div>

          {/* Search Input */}
          <div className="relative mb-8">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search for anything..."
                className="w-full px-6 py-6 text-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-soft focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100"
              />
              {loading && (
                <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-celadon-600"></div>
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showSuggestions && query.trim() && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-strong z-50 max-h-96 overflow-y-auto">
                {results.length === 0 && !loading && query.trim().length >= 2 && (
                  <div className="p-8 text-center text-gray-500">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <SearchIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-lg font-medium mb-2">No results found</p>
                    <p className="text-sm">Try a different search term or check your spelling</p>
                  </div>
                )}

                {results.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className={`flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 first:rounded-t-2xl last:rounded-b-2xl ${
                      index === selectedIndex ? 'bg-celadon-50' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      result.type === 'project' ? 'bg-blue-100 text-blue-600' :
                      result.type === 'prompt' ? 'bg-yellow-100 text-yellow-600' :
                      result.type === 'experiment' ? 'bg-purple-100 text-purple-600' :
                      result.type === 'flow' ? 'bg-indigo-100 text-indigo-600' :
                      'bg-green-100 text-green-600'
                    }`}>
                      {getResultIcon(result.type)}
                    </div>
                    
                    <div className="ml-4 flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {result.name}
                        </p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getTypeLabel(result.type)}
                        </span>
                      </div>
                      {result.description && (
                        <p className="text-sm text-gray-600 truncate mb-1">
                          {result.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {result.project_name && result.type !== 'project' && (
                          <span>in {result.project_name}</span>
                        )}
                        <span className="flex items-center">
                          <ClockIcon className="mr-1" />
                          {formatRelativeTime(result.updated_at || result.created_at)}
                        </span>
                      </div>
                    </div>
                    
                    <ArrowRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Searches */}
          {!query.trim() && recentSearches.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Recent Searches</h2>
                <button
                  onClick={clearRecentSearches}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-medium"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-3">
                {recentSearches.map((searchTerm, index) => (
                  <button
                    key={index}
                    onClick={() => handleRecentSearchClick(searchTerm)}
                    className="flex items-center w-full p-4 text-left bg-white rounded-xl hover:bg-gray-50 transition-colors duration-150 group shadow-soft"
                  >
                    <SearchIcon className="w-5 h-5 text-gray-400 mr-4" />
                    <span className="text-gray-700 group-hover:text-gray-900 font-medium">{searchTerm}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Tips */}
          {!query.trim() && recentSearches.length === 0 && (
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Search your workspace</h2>
              <p className="text-lg text-gray-600 mb-12">Find projects, prompts, experiments, test sets, and flows quickly</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-soft">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <FolderIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Projects</p>
                    <p className="text-sm text-gray-600">Find your workspaces</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-soft">
                  <div className="w-12 h-12 bg-celadon-200 rounded-xl flex items-center justify-center">
                    <ZapIcon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Prompts</p>
                    <p className="text-sm text-gray-600">Search prompt templates</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-soft">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <FlaskIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Experiments</p>
                    <p className="text-sm text-gray-600">Find A/B tests</p>
                  </div>
                </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-4xl mx-auto my-6">
                <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-soft">
                  <div className="w-12 h-12 bg-celadon-200 rounded-xl flex items-center justify-center">
                    <DatabaseIcon className="w-6 h-6 text-celadon-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Test Sets</p>
                    <p className="text-sm text-gray-600">Find evaluation data</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-soft md:col-span-2 lg:col-span-1">
                  <div className="w-12 h-12 bg-celadon-200 rounded-xl flex items-center justify-center">
                    <FlowIcon className="w-6 h-6 text-celadon-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Flows</p>
                    <p className="text-sm text-gray-600">Find composer workflows</p>
                  </div>
                </div>
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default Search; 